import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Plus, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TemplateCard } from "@/components/TemplateCard";
import { TemplateDialog } from "@/components/TemplateDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { RefreshIndicator } from "@/components/RefreshIndicator";
import { DateFilter } from "@/components/DateFilter";
import { QR } from "@/constants/testIds";

const periodKey = (t) => (t.year ? t.year * 100 + (t.months?.[0] || 0) : null);

const Chip = ({ active, onClick, children, id }) => (
  <button onClick={onClick} data-testid={`${QR.chip}-${id}`}
    className={`shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors ${active ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"}`}>
    {children}
  </button>
);

export default function QuickReply() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user.role);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [dateF, setDateF] = useState({ month: "all", year: "all", sort: "newest" });
  const [lastLoaded, setLastLoaded] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, template: null });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async (manual) => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([api.get("/templates"), api.get("/categories")]);
      setTemplates(t.data);
      setCategories(c.data);
      setLastLoaded(new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
      if (manual === true) toast.success("Data diperbarui");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const years = useMemo(() => {
    const set = new Set(templates.map((t) => t.year).filter(Boolean));
    set.add(new Date().getFullYear());
    return [...set].sort((a, b) => b - a);
  }, [templates]);

  const catName = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.name])), [categories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = templates.filter((t) => {
      if (cat === "none" ? t.category_id : cat !== "all" && t.category_id !== cat) return false;
      if (dateF.month !== "all" && !(t.months || []).includes(Number(dateF.month))) return false;
      if (dateF.year !== "all" && t.year !== Number(dateF.year)) return false;
      if (!q) return true;
      return [t.title, t.content, ...(t.keywords || [])].some((s) => s.toLowerCase().includes(q));
    });
    const byPeriod = (dir) => (a, b) => {
      const ka = periodKey(a), kb = periodKey(b);
      if (ka === kb) return 0;
      if (ka === null) return 1;
      if (kb === null) return -1;
      return dir * (ka - kb);
    };
    const sorters = {
      newest: byPeriod(-1),
      oldest: byPeriod(1),
      title: (a, b) => a.title.localeCompare(b.title, "id"),
    };
    return list.sort(sorters[dateF.sort]);
  }, [templates, query, cat, dateF]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/templates/${toDelete.id}`);
      toast.success("Template dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  const hasUncategorized = templates.some((t) => !t.category_id);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
        <div>
          <p className="overline-label">Program CS</p>
          <h1 className="font-heading mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Balasan Cepat</h1>
          <p className="mt-2 text-sm text-slate-500">{isAdmin ? "Kelola dan salin template jawaban untuk tim CS." : "Cari template, lalu salin jawaban dengan satu klik."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RefreshIndicator loading={loading} lastLoaded={lastLoaded} onRefresh={() => load(true)} />
          {isAdmin && (
            <Button onClick={() => setDialog({ open: true, template: null })} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid={QR.addButton}>
              <Plus className="h-4 w-4 mr-1" /> Tambah Template
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 sm:p-5 fade-up" style={{ animationDelay: "60ms" }}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari judul, isi jawaban, atau kata kunci..." className="h-12 pl-11 text-base bg-slate-50 border-slate-200" data-testid={QR.search} />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
          <Chip id="all" active={cat === "all"} onClick={() => setCat("all")}>Semua</Chip>
          {categories.map((c) => <Chip key={c.id} id={c.id} active={cat === c.id} onClick={() => setCat(c.id)}>{c.name}</Chip>)}
          {hasUncategorized && <Chip id="none" active={cat === "none"} onClick={() => setCat("none")}>Tanpa kategori</Chip>}
        </div>
        <div className="border-t border-slate-100 pt-4">
          <DateFilter {...dateF} years={years} onChange={(p) => setDateF((f) => ({ ...f, ...p }))} />
        </div>
      </div>

      <p className="text-xs text-slate-500" data-testid={QR.count}>{filtered.length} template ditemukan</p>

      {filtered.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white py-20 text-center" data-testid={QR.empty}>
          <Inbox className="h-10 w-10 text-slate-300" />
          <p className="mt-4 font-medium text-slate-700">{templates.length === 0 ? "Belum ada template" : "Tidak ada template yang cocok"}</p>
          <p className="mt-1 text-sm text-slate-500">{templates.length === 0 && isAdmin ? "Klik \"Tambah Template\" untuk membuat yang pertama." : "Coba kata kunci atau kategori lain."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {filtered.map((t, i) => (
            <TemplateCard key={t.id} tpl={t} index={i} categoryName={catName[t.category_id]} isAdmin={isAdmin}
              onEdit={(tpl) => setDialog({ open: true, template: tpl })} onDelete={setToDelete} />
          ))}
        </div>
      )}

      {isAdmin && (
        <>
          <TemplateDialog open={dialog.open} template={dialog.template} categories={categories}
            onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={load} />
          <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={confirmDelete}
            title="Hapus template?" description={`Template "${toDelete?.title}" akan dihapus permanen untuk semua pengguna.`} />
        </>
      )}
    </div>
  );
}
